import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import {
  CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem,
} from "@/components/ui/command";
import { LayoutDashboard, GitBranch, Library, BarChart3, Users, FileText, Building2, Layers } from "lucide-react";

const NAV_ACTIONS = [
  { label: "Go to Plan of the Day", to: "/dashboard", icon: LayoutDashboard },
  { label: "Go to Pipeline", to: "/pipeline", icon: GitBranch },
  { label: "Go to Bid History", to: "/analytics", icon: BarChart3 },
  { label: "Go to Libraries", to: "/libraries", icon: Library },
  { label: "Go to Team", to: "/team", icon: Users },
];

export default function CommandPalette({ open, setOpen }) {
  const navigate = useNavigate();
  const [bids, setBids] = useState([]);
  const [clients, setClients] = useState([]);
  const [query, setQuery] = useState("");
  const [hits, setHits] = useState([]);
  const timer = useRef(null);

  useEffect(() => {
    if (!open) return;
    setQuery(""); setHits([]);
    api.get("/bids").then((r) => setBids(r.data.slice(0, 40))).catch(() => {});
    api.get("/clients").then((r) => setClients(r.data.slice(0, 40))).catch(() => {});
  }, [open]);

  // Debounced deep search across sheets (number / name / OCR text) and documents.
  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    const term = query.trim();
    if (term.length < 2) { setHits([]); return; }
    timer.current = setTimeout(async () => {
      try { const { data } = await api.get("/search", { params: { q: term } }); setHits(data.results || []); }
      catch { setHits([]); }
    }, 220);
    return () => timer.current && clearTimeout(timer.current);
  }, [query]);

  const go = (to) => { setOpen(false); navigate(to); };
  const openHit = (r) => {
    setOpen(false);
    if (r.type === "sheet") {
      navigate(`/bids/${r.bid_id}?tab=takeoff&sheet=${r.sheet_id}&q=${encodeURIComponent(query.trim())}`);
    } else {
      navigate(`/bids/${r.bid_id}?tab=documents`);
    }
  };

  return (
    <CommandDialog open={open} onOpenChange={setOpen} shouldFilter={false}>
      <CommandInput placeholder="Search bids, drawings, sheet numbers, notes…" value={query} onValueChange={setQuery} data-testid="command-palette-input" />
      <CommandList>
        <CommandEmpty>No results found.</CommandEmpty>
        {hits.length > 0 && (
          <CommandGroup heading="In your drawings & docs">
            {hits.map((r, i) => (
              <CommandItem key={`hit-${i}`} value={`hit-${i}`} onSelect={() => openHit(r)} data-testid={`palette-hit-${r.sheet_id || r.document_id}`}>
                {r.type === "sheet" ? <Layers className="w-4 h-4 mr-2 text-[#003E87] shrink-0" /> : <FileText className="w-4 h-4 mr-2 text-slate-500 shrink-0" />}
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 truncate">
                    {r.sheet_number ? <span className="font-mono text-xs text-[#003E87] shrink-0">{r.sheet_number}</span> : null}
                    <span className="truncate">{r.name}</span>
                  </div>
                  {r.snippet && <div className="text-[11px] text-slate-400 truncate">{r.snippet}</div>}
                </div>
                <span className="ml-2 text-[11px] text-slate-400 shrink-0 truncate max-w-[40%]">{r.bid_name}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        <CommandGroup heading="Navigate">
          {NAV_ACTIONS.filter((a) => matches(a.label, query)).map((a) => (
            <CommandItem key={a.to} value={a.to} onSelect={() => go(a.to)}>
              <a.icon className="w-4 h-4 mr-2 text-slate-500" />
              {a.label}
            </CommandItem>
          ))}
        </CommandGroup>
        {bids.filter((b) => matches(b.name, query)).length > 0 && (
          <CommandGroup heading="Bids">
            {bids.filter((b) => matches(b.name, query)).slice(0, 12).map((b) => (
              <CommandItem key={b.bid_id} value={b.bid_id} onSelect={() => go(`/bids/${b.bid_id}`)}>
                <FileText className="w-4 h-4 mr-2 text-slate-500" />
                <span className="truncate">{b.name}</span>
                <span className="ml-auto text-xs text-slate-400">{b.status}</span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}
        {clients.filter((c) => matches(c.name, query)).length > 0 && (
          <CommandGroup heading="Clients">
            {clients.filter((c) => matches(c.name, query)).slice(0, 12).map((c) => (
              <CommandItem key={c.client_id} value={c.client_id} onSelect={() => go("/pipeline")}>
                <Building2 className="w-4 h-4 mr-2 text-slate-500" />
                {c.name}
              </CommandItem>
            ))}
          </CommandGroup>
        )}
      </CommandList>
    </CommandDialog>
  );
}

function matches(text, q) {
  const s = (q || "").trim().toLowerCase();
  if (!s) return true;
  return (text || "").toLowerCase().includes(s);
}
